import React, { useState, useEffect } from "react";
import axios from "../../api/axios_client";
import "./UsersPage.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const UsersPage = () => {
  const navigate = useNavigate();
  const countries = [
    "Algeria",
    "Bahrain",
    "Comoros",
    "Djibouti",
    "Egypt",
    "Iraq",
    "Jordan",
    "Kuwait",
    "Lebanon",
    "Libya",
    "Mauritania",
    "Morocco",
    "Oman",
    "Palestine",
    "Qatar",
    "Saudi Arabia",
    "Somalia",
    "Sudan",
    "Syria",
    "Tunisia",
    "United Arab Emirates",
    "Yemen",
  ];

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    userId: null,
  });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    country: null,
    access: "2",
    first_name: "",
    middle_name: "",
    last_name: "",
    position: "",
    phone_number: "",
  });
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "id",
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(5);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/admin/users");
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError("Failed to fetch users. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Count super admins
  const superAdminCount = users.filter((user) => user.access === "3").length;
  const isSuperAdminLimitReached = superAdminCount >= 2;

  // Filter users based on search term
  const filteredUsers = users.filter(
    (user) =>
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === "ascending" ? 1 : -1;
    }
    return 0;
  });

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const addUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post("/api/admin/users", newUser);
      setUsers([...users, response.data]);
      setNewUser({
        username: "",
        email: "",
        password: "",
        access: "2",
        country: newUser.country,
        first_name: "",
        middle_name: "",
        last_name: "",
        position: "",
        phone_number: "",
      });
      toast.success("User added successfully!"); 
      setAddUserDialog(false);
      setError(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to add user. Please try again.";
      toast.error(errorMessage); 
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id) => {
    setDeleteLoading(true); // Add this line
    try {
      await axios.delete(`/api/admin/users`, { data: { ids: [id] } });
      setUsers(users.filter((user) => user.id !== id));
      setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
      setSuccessMessage("User deleted successfully!");
      setError(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to delete user. Please try again.";
      setError(errorMessage);
    } finally {
      setDeleteLoading(false); // Add this line
      setDeleteDialog({ open: false, userId: null });
    }
  };

  const handleRowClick = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const startEditing = (user) => {
    setEditingUser(user);
  };

  const saveEdit = async () => {
    setLoading(true);
    try {
      await axios.put(`/api/admin/users/${editingUser.id}`, editingUser);
      setUsers(
        users.map((user) => (user.id === editingUser.id ? editingUser : user))
      );
      setSuccessMessage("User updated successfully!");
      setError(null);
    } catch (err) {
      setError("Failed to update user. Please try again.");
    } finally {
      setLoading(false);
      setEditingUser(null);
    }
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "ascending"
          ? "descending"
          : "ascending",
    });
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId]
    );
  };

  const handleSelectAll = () => {
    setSelectedUsers(
      selectedUsers.length === currentUsers.length
        ? []
        : currentUsers.map((user) => user.id)
    );
  };

  const bulkDelete = async () => {
    setLoading(true);
    try {
      await axios.delete(`/api/admin/users`, { data: { ids: selectedUsers } });
      setUsers(users.filter((user) => !selectedUsers.includes(user.id)));
      setSelectedUsers([]);
      setSuccessMessage("Selected users deleted successfully!");
      setError(null);
    } catch (err) {
      setError("Failed to delete selected users. Please try again.");
    } finally {
      setLoading(false);
      setBulkDeleteDialog(false);
    }
  };

  return (
    <div className="users-page">
      {/* Success Message */}
      {successMessage && (
        <div className="alert alert-success">
          {successMessage}
          <button
            onClick={() => setSuccessMessage(null)}
            className="alert-close"
          >
            ×
          </button>
        </div>
      )}

      <div className="users-actions">
        <button
          onClick={() => setAddUserDialog(true)}
          className="btn btn-primary"
        >
          Add New User
        </button>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="table-actions">
        <button
          onClick={() => setBulkDeleteDialog(true)}
          className="btn btn-danger"
          disabled={selectedUsers.length === 0 || loading}
        >
          Delete Selected
        </button>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <table className="users-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectedUsers.length === currentUsers.length}
                onChange={handleSelectAll}
              />
            </th>
            <th onClick={() => handleSort("id")}>
              ID{" "}
              {sortConfig.key === "id" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("first_name")}>
              First Name{" "}
              {sortConfig.key === "first_name" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("middle_name")}>
              Middle Name{" "}
              {sortConfig.key === "middle_name" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("last_name")}>
              Last Name{" "}
              {sortConfig.key === "last_name" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("username")}>
              Username{" "}
              {sortConfig.key === "username" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("email")}>
              Email{" "}
              {sortConfig.key === "email" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("phone_number")}>
              Phone{" "}
              {sortConfig.key === "phone_number" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("position")}>
              Position{" "}
              {sortConfig.key === "position" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("country")}>
              Country{" "}
              {sortConfig.key === "country" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("access")}>
              Role{" "}
              {sortConfig.key === "access" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
            <th onClick={() => handleSort("active")}>
              Active{" "}
              {sortConfig.key === "active" &&
                (sortConfig.direction === "ascending" ? "▲" : "▼")}
            </th>
          </tr>
        </thead>
        <tbody>
          {currentUsers.map((user) => (
            <tr
              key={user.id}
              onClick={() => handleRowClick(user.id)}
              style={{ cursor: "pointer" }}
              className="user-row"
            >
              <td>
                <input
                  type="checkbox"
                  checked={selectedUsers.includes(user.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectUser(user.id);
                  }}
                />
              </td>
              <td>{user.id}</td>
              <td>{user.first_name || "N/A"}</td>
              <td>{user.middle_name || "N/A"}</td>
              <td>{user.last_name || "N/A"}</td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>{user.phone_number || "N/A"}</td>
              <td>{user.position || "N/A"}</td>
              <td>{user.country ?? "N/A"}</td>
              <td>
                {user.access === "1"
                  ? "User"
                  : user.access === "2"
                  ? "Country Admin"
                  : "Super Admin"}
              </td>
              <td>{user.active ? "Yes" : "No"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pagination">
        {Array.from(
          { length: Math.ceil(sortedUsers.length / usersPerPage) },
          (_, i) => (
            <button
              key={i}
              onClick={() => paginate(i + 1)}
              className={`btn ${
                currentPage === i + 1 ? "btn-primary" : "btn-secondary"
              }`}
            >
              {i + 1}
            </button>
          )
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialog.open && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete this user?</p>
            <div className="dialog-actions">
              <button
                onClick={() => deleteUser(deleteDialog.userId)}
                className="btn btn-danger"
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={() => setDeleteDialog({ open: false, userId: null })}
                className="btn btn-secondary"
                disabled={deleteLoading} // Add this line
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {addUserDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            {error && (
              <div className="alert alert-error">
                {error}
                <button onClick={() => setError(null)} className="alert-close">
                  ×
                </button>
              </div>
            )}
            <h3>Add New User</h3>
            <form onSubmit={addUser} className="user-form">
              <div className="form-section">
                <h3>Account Information</h3>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="Username"
                    value={newUser.username}
                    onChange={(e) =>
                      setNewUser({ ...newUser, username: e.target.value })
                    }
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    value={newUser.email}
                    onChange={(e) =>
                      setNewUser({ ...newUser, email: e.target.value })
                    }
                    required
                  />
                  <input
                    type="password"
                    placeholder="Password"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    required
                  />
                  <select
                    value={newUser.access}
                    onChange={(e) =>
                      setNewUser({ ...newUser, access: e.target.value })
                    }
                  >
                    <option value="2">Country Admin</option>
                    <option value="3" disabled={isSuperAdminLimitReached}>
                      Super Admin{" "}
                      {isSuperAdminLimitReached ? "(Limit Reached)" : ""}
                    </option>
                  </select>
                  {isSuperAdminLimitReached && newUser.access !== "3" && (
                    <small
                      style={{
                        color: "#666",
                        fontSize: "0.8em",
                        display: "block",
                      }}
                    >
                      Only 2 Super Admins are allowed in the system.
                    </small>
                  )}
                </div>
              </div>

              <div className="form-section">
                <h3>Personal Information</h3>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="First Name"
                    value={newUser.first_name}
                    onChange={(e) =>
                      setNewUser({ ...newUser, first_name: e.target.value })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Middle Name"
                    value={newUser.middle_name}
                    onChange={(e) =>
                      setNewUser({ ...newUser, middle_name: e.target.value })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last Name"
                    value={newUser.last_name}
                    onChange={(e) =>
                      setNewUser({ ...newUser, last_name: e.target.value })
                    }
                    required
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={newUser.phone_number}
                    onChange={(e) =>
                      setNewUser({ ...newUser, phone_number: e.target.value })
                    }
                    required
                  />
                  <small
                    style={{
                      color: "#666",
                      fontSize: "0.8em",
                      marginTop: "-10px",
                      display: "block",
                    }}
                  >
                    Please include country code (e.g., +962)
                  </small>
                  {newUser.access === "2" && (
                    <select
                      value={newUser.country}
                      onChange={(e) =>
                        setNewUser({ ...newUser, country: e.target.value })
                      }
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {newUser.access !== "3" && (
                <div className="form-section">
                  <h3>Professional Information</h3>
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Position"
                      value={newUser.position}
                      onChange={(e) =>
                        setNewUser({ ...newUser, position: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
              )}

              <div className="dialog-actions">
                <button
                  type="button"
                  onClick={() => setAddUserDialog(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteDialog && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h2>Confirm Bulk Delete</h2>
            <p>Are you sure you want to delete {selectedUsers.length} users?</p>
            <div className="dialog-actions">
              <button
                onClick={bulkDelete}
                className="btn btn-danger"
                disabled={loading}
              >
                {loading ? "Deleting..." : "Delete All"}
              </button>
              <button
                onClick={() => setBulkDeleteDialog(false)}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
